import { useCallback, useEffect, useRef } from 'react';

/**
 * Decouples an input's own paint from the parent's recalc pipeline.
 *
 * An editable grid cell owns its display value via local state, so a keystroke
 * paints instantly — UNLESS the same handler also calls onRowChange synchronously.
 * onRowChange triggers the parent's heavy work (deep clone + totals recalc + a
 * context-wide re-render), and React batches that into the SAME commit as the
 * input's local update, so the browser cannot paint the typed character until the
 * recalc finishes. That is the visible typing lag.
 *
 * This hook keeps the local update urgent and commits to the parent on the next
 * animation frame, coalescing a burst of keystrokes into a single commit. The
 * returned `flush` runs any pending commit immediately (call it on blur so leaving
 * the field never strands an un-committed value).
 *
 * @param {Function} onRowChange (rowId, field, value) => void
 * @returns {[Function, Function]} [commit, flush]
 */
function useDeferredRowChange(onRowChange) {
  const frameRef = useRef(0);
  const pendingRef = useRef(null);
  const onRowChangeRef = useRef(onRowChange);
  onRowChangeRef.current = onRowChange;

  const run = useCallback(() => {
    frameRef.current = 0;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) onRowChangeRef.current?.(pending.rowId, pending.field, pending.value);
  }, []);

  const commit = useCallback(
    (rowId, field, value) => {
      pendingRef.current = { rowId, field, value };
      if (frameRef.current) return;
      frameRef.current = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : 0;
      /* Environments without rAF (tests/SSR) commit synchronously. */
      if (!frameRef.current) run();
    },
    [run],
  );

  const flush = useCallback(() => {
    if (frameRef.current && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
    run();
  }, [run]);

  useEffect(
    () => () => {
      if (frameRef.current && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return [commit, flush];
}

export default useDeferredRowChange;
